import { forwardRef, useCallback, useEffect, useState } from "react";

/**
 * A custom version of the ColorInput component from react-colorful to support input ref.
 * Ported via https://github.com/omgovich/react-colorful/blob/master/src/components/common/ColorInput.tsx
 * Issue tracking: https://github.com/omgovich/react-colorful/issues/60
 */

interface ColorInputProps
  extends Omit<
    React.HTMLAttributes<HTMLInputElement>,
    "color" | "onChange" | "onChangeCapture"
  > {
  alpha?: boolean;
  color?: string;
  onChange?: (value: string) => void;
}

const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>(
  (props, ref) => {
    const { color = "", onChange, onBlur, alpha, ...rest } = props;
    const [value, setValue] = useState(color);

    const escape = useCallback(
      (value: string) =>
        value.replace(/([^0-9A-F]+)/gi, "").substring(0, alpha ? 8 : 6),
      [alpha]
    );
    const validate = useCallback(
      (value: string) => validHex(value, alpha),
      [alpha]
    );
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = escape(e.target.value);
        setValue(inputValue);
        if (validate(inputValue)) onChange?.(prefix(inputValue));
      },
      [escape, validate, onChange]
    );
    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (!validate(e.target.value)) setValue(escape(color));
        onBlur?.(e);
      },
      [color, escape, validate, onBlur]
    );

    useEffect(() => {
      setValue(escape(color));
    }, [color, escape]);

    return (
      <input
        {...rest}
        ref={ref}
        value={prefix(value)}
        spellCheck={false}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  }
);

const matcher = /^#?([0-9A-F]{3,8})$/i;
export const validHex = (
  value: string | undefined,
  alpha?: boolean
): boolean => {
  if (!value) return false;
  const match = matcher.exec(value);
  const length = match ? match[1].length : 0;

  return (
    length === 3 || // '#rgb' format
    length === 6 || // '#rrggbb' format
    (!!alpha && length === 4) || // '#rgba' format
    (!!alpha && length === 8) // '#rrggbbaa' format
  );
};
const prefix = (value: string) => "#" + value;

export default ColorInput;
